import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Zap, Eye, EyeOff, BookOpen } from "lucide-react";
import { ThemeToggle, Theme } from "./ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { setTheme, toggleIsPrivate } from '@/store/slices/uiSlice';

interface HeaderProps {
  onLogoClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onLogoClick }) => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.ui.theme);
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = window.document.documentElement;
      root.classList.remove('dark', 'light-simple');
      if (theme !== 'default') {
        root.classList.add(theme);
      }
    }
  }, [theme]);

  return (
    <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center bg-card p-6 rounded-2xl shadow-sm border border-border gap-4 transition-colors duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between w-full lg:w-auto gap-4">
        <div onClick={onLogoClick} className={onLogoClick ? 'cursor-pointer select-none' : ''}>
          <h1 className="text-2xl font-black flex items-center gap-2 text-foreground hover:text-primary transition-colors">
            <Zap className="w-6 h-6 text-primary" />
            DCA Pro 대시보드
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            최적화된 알고리즘으로 스마트하게 트레이딩하세요.{' '} &nbsp;&nbsp;
            <a href="/manual/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-semibold text-xs border border-primary/30 rounded-lg px-2 py-1 hover:bg-primary/5 transition-colors">
              <BookOpen className="w-3.5 h-3.5" />
              매뉴얼 보기
            </a>
          </p>
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => dispatch(toggleIsPrivate())}
            className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
          >
            {isPrivate ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </Button>
          <ThemeToggle theme={theme} onThemeChange={(t) => dispatch(setTheme(t))} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
        <div className="hidden lg:flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => dispatch(toggleIsPrivate())}
            className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
          >
            {isPrivate ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <div className="h-4 w-px bg-border mx-1" />
          <ThemeToggle theme={theme} onThemeChange={(t) => dispatch(setTheme(t))} />
        </div>
        <div className="h-4 w-px bg-border hidden lg:block mx-2" />
        {/* <Badge variant="secondary" className="px-3 py-1 bg-success/10 text-success border-none font-bold whitespace-nowrap">연결됨: Binance</Badge> */}
      </div>
    </header>
  );
};
